<script context="module">
  export const SPLIT_LAYOUT_VARIANTS = /** @type { const } */ {
    LAYOUT_50_50: "50/50",
    LAYOUT_25_75: "25/75",
    LAYOUT_75_25: "75/25"
  };

  export const SPLIT_LAYOUT_RIGHT_GRID_COLUMNS = /** @type {const} **/ ({
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: { mobile: 4, tablet: 12, desktop: 6 },
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: { mobile: 4, tablet: 7, desktop: 9 },
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: { mobile: 4, tablet: 5, desktop: 3 }
  });
</script>

<script>
  import Grid from "../atoms/Grid.svelte";
  import GridContainer from "../atoms/GridContainer.svelte";
  import GridDivider from "../atoms/GridDivider.svelte";

  /**
   * Additional classes which apply to the container
   * @type { string }
   * */
  let className = "";
  export { className as class };

  /**
   * Primarily used for testing
   * @type {string}
   * */
  export let id;

  /** The class name for the sticky container height
   * @type {string}
   * */
  export let stickyContainerHeightClass =
    "lg:h-[calc(100vh-120px)] lg:max-h-[780px] xl:max-h-[960px]";

  /**
   * Determines whether the layout should be sticky.
   * @type {boolean}
   * */
  export let isSticky;

  /**
   * The image variant of ImageGrid (all images will be the same aspect ratio)
   * @type  { typeof SPLIT_LAYOUT_VARIANTS }
   */
  export let variant = SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50;

  const isHalf = variant === SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50;

  const itemsLeftClasses = {
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "lg:col-span-6",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-5 lg:col-span-3",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "sm:max-md:row-start-2 md:col-span-7 lg:col-span-9"
  };
  const itemsRightClasses = {
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "lg:col-span-6",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:col-span-7 lg:col-span-9",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "sm:max-md:row-start-1 md:col-span-5 lg:col-span-3"
  };
  const stickyClasses = {
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]: "lg:sticky lg:top-15",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]: "md:sticky md:top-15",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]: "md:sticky md:top-15"
  };
  const stickyHeightClasses = {
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_50_50]:
      "lg:h-[calc(100vh-120px)] lg:max-h-[780px] xl:max-h-[960px]",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_25_75]:
      "md:h-[calc(100vh-120px)] md:max-h-[780px] xl:max-h-[960px]",
    [SPLIT_LAYOUT_VARIANTS.LAYOUT_75_25]:
      "md:h-[calc(100vh-120px)] md:max-h-[780px] xl:max-h-[960px]"
  };

  const stickyWrapperClasses = isSticky ? stickyClasses[variant] : "";
  const stickyHeightClass = isSticky
    ? `${stickyHeightClasses[variant]} ${stickyContainerHeightClass}`
    : "";
  const leftWrapperClasses = itemsLeftClasses[variant];
  const rightWrapperClasses = itemsRightClasses[variant];

  const overflowClasses = isSticky ? "overflow-y-auto pl-3 -ml-3" : "pl-3 -ml-3"; // We need to add "fake" padding to account for the bullet in the tertiary nav.
</script>

<GridContainer {id} containerClass={className}>
  <div class="relative">
    {#if isHalf}
      <GridDivider numberOfColumns={2} containerClasses="hidden lg:grid" />
    {/if}
    <Grid gridClass="relative max-lg:gap-y-15" id={`${id}-grid`}>
      <div
        class="col-full-width {overflowClasses} {leftWrapperClasses} {stickyWrapperClasses} {stickyHeightClass}"
      >
        <slot name="left" />
      </div>
      <div class="col-full-width {rightWrapperClasses}">
        <slot name="right" />
      </div>
    </Grid>
  </div>
</GridContainer>
